import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useIsPresent } from "@tamagui/animate-presence";
import { useComposedRefs } from "@tamagui/compose-refs";
import { isWeb } from "@tamagui/constants";
import { Theme, View, createStyledContext, styled, useConfiguration, useEvent, useThemeName } from "@tamagui/core";
import { Dismissable } from "@tamagui/dismissable";
import { composeEventHandlers } from "@tamagui/helpers";
import { PortalItem } from "@tamagui/portal";
import { YStack } from "@tamagui/stacks";
import * as React from "react";
import { TOAST_NAME } from "./constants";
import { ToastAnnounce } from "./ToastAnnounce";
import { Collection, useToastProviderContext } from "./ToastProvider";
import { VIEWPORT_PAUSE, VIEWPORT_RESUME } from "./ToastViewport";

var __require = /* @__PURE__ */ ((x) => typeof require !== "undefined" ? require : typeof Proxy !== "undefined" ? new Proxy(x, { get: (a, b) => (typeof require !== "undefined" ? require : a)[b] }) : x)(function(x) {
	if (typeof require !== "undefined") return require.apply(this, arguments);
	throw Error("Calling `require` for \"" + x + "\" in an environment that doesn't expose the `require` function. See https://rolldown.rs/in-depth/bundling-cjs#require-external-modules for more details.");
});

var getPanResponder = function() {
	return require("react-native").PanResponder;
};
var ToastImplFrame = styled(YStack, {
	name: "ToastImpl",
	tabIndex: 0,
	variants: { unstyled: { false: {
		focusStyle: {
			outlineStyle: "solid",
			outlineWidth: 2,
			outlineColor: "$outlineColor"
		},
		backgroundColor: "$color6",
		borderRadius: "$4",
		paddingHorizontal: "$4",
		paddingVertical: "$3",
		marginHorizontal: "auto",
		marginVertical: "$1",
		elevation: "$3"
	} } },
	defaultVariants: { unstyled: process.env.TAMAGUI_HEADLESS === "1" }
});
var { Provider: ToastInteractiveProvider, useStyledContext: useToastInteractiveContext } = createStyledContext({ onClose() {} });
var ToastImpl = /* @__PURE__ */ React.forwardRef(function(props, forwardedRef) {
	var { scope, type = "foreground", duration: durationProp, open, onClose, onEscapeKeyDown, onPause, onResume, onSwipeStart, onSwipeMove, onSwipeCancel, onSwipeEnd, viewportName = "default", ...toastProps } = props;
	var isPresent = useIsPresent();
	var context = useToastProviderContext(scope);
	var [node, setNode] = React.useState(null);
	var composedRefs = useComposedRefs(forwardedRef, setNode);
	var duration = durationProp || context.duration;
	var closeTimerStartTimeRef = React.useRef(0);
	var closeTimerRemainingTimeRef = React.useRef(duration);
	var closeTimerRef = React.useRef(0);
	var { onToastAdd, onToastRemove } = context;
	var viewport = React.useMemo(function() {
		return context.viewports[viewportName];
	}, [context.viewports, viewportName]);
	var handleClose = useEvent(function() {
		if (!isPresent) return;
		if (isWeb) {
			if (node === null || node === void 0 ? void 0 : node.contains(document.activeElement)) viewport === null || viewport === void 0 || viewport.focus();
		}
		onClose();
	});
	var startTimer = React.useCallback(function(duration2) {
		if (!duration2 || duration2 === Number.POSITIVE_INFINITY) return;
		clearTimeout(closeTimerRef.current);
		closeTimerStartTimeRef.current = (/* @__PURE__ */ new Date()).getTime();
		closeTimerRef.current = setTimeout(handleClose, duration2);
	}, [handleClose]);
	var handleResume = React.useCallback(function() {
		startTimer(closeTimerRemainingTimeRef.current);
		onResume === null || onResume === void 0 || onResume();
	}, [onResume, startTimer]);
	var handlePause = React.useCallback(function() {
		var elapsedTime = (/* @__PURE__ */ new Date()).getTime() - closeTimerStartTimeRef.current;
		closeTimerRemainingTimeRef.current = closeTimerRemainingTimeRef.current - elapsedTime;
		window.clearTimeout(closeTimerRef.current);
		onPause === null || onPause === void 0 || onPause();
	}, [onPause]);
	React.useEffect(function() {
		if (!isWeb) return;
		if (viewport) {
			viewport.addEventListener(VIEWPORT_PAUSE, handlePause);
			viewport.addEventListener(VIEWPORT_RESUME, handleResume);
			return function() {
				viewport.removeEventListener(VIEWPORT_PAUSE, handlePause);
				viewport.removeEventListener(VIEWPORT_RESUME, handleResume);
			};
		}
	}, [
		viewport,
		duration,
		onPause,
		onResume,
		startTimer
	]);
	React.useEffect(function() {
		if (open && !context.isClosePausedRef.current) startTimer(duration);
	}, [
		open,
		duration,
		context.isClosePausedRef,
		startTimer
	]);
	React.useEffect(function() {
		onToastAdd();
		return function() {
			return onToastRemove();
		};
	}, [onToastAdd, onToastRemove]);
	var announceTextContent = React.useMemo(function() {
		if (!isWeb) return null;
		return node ? getAnnounceTextContent(node) : null;
	}, [node]);
	var isHorizontalSwipe = [
		"left",
		"right",
		"horizontal"
	].includes(context.swipeDirection);
	var { animationDriver } = useConfiguration();
	if (!animationDriver) throw new Error("Must set animations in tamagui.config.ts");
	var { useAnimatedNumber, useAnimatedNumberStyle } = animationDriver;
	var animatedNumber = useAnimatedNumber(0);
	var _animationDriver_NumberView, _ref;
	var AnimatedView = (_ref = (_animationDriver_NumberView = animationDriver["NumberView"]) !== null && _animationDriver_NumberView !== void 0 ? _animationDriver_NumberView : animationDriver.View) !== null && _ref !== void 0 ? _ref : View;
	var animatedStyles = useAnimatedNumberStyle(animatedNumber, function(val) {
		"worklet";
		return { transform: [isHorizontalSwipe ? { translateX: val } : { translateY: val }] };
	});
	var panResponder = React.useMemo(function() {
		var PanResponder = getPanResponder();
		if (!PanResponder) return null;
		return PanResponder.create({
			onMoveShouldSetPanResponder: function(e, gesture) {
				if (shouldGrantGestureMove(context.swipeDirection, gesture)) {
					onSwipeStart === null || onSwipeStart === void 0 || onSwipeStart(e);
					return true;
				}
				return false;
			},
			onPanResponderGrant: function(e) {
				if (!isWeb) handlePause === null || handlePause === void 0 || handlePause();
			},
			onPanResponderMove: function(e, gesture) {
				var { x, y } = getGestureDistance(context.swipeDirection, gesture);
				var delta = {
					x,
					y
				};
				animatedNumber.setValue(isHorizontalSwipe ? x : y, { type: "direct" });
				if (isDeltaInDirection(delta, context.swipeDirection, context.swipeThreshold)) onSwipeEnd === null || onSwipeEnd === void 0 || onSwipeEnd(e);
				onSwipeMove === null || onSwipeMove === void 0 || onSwipeMove(e);
			},
			onPanResponderEnd: function(e, param) {
				var { dx, dy } = param;
				if (!isDeltaInDirection({
					x: dx,
					y: dy
				}, context.swipeDirection, context.swipeThreshold)) {
					if (!isWeb) handleResume === null || handleResume === void 0 || handleResume();
					onSwipeCancel === null || onSwipeCancel === void 0 || onSwipeCancel(e);
					animatedNumber.setValue(0, { type: "spring" });
				}
			}
		});
	}, [handlePause, handleResume]);
	var themeName = useThemeName();
	return /* @__PURE__ */ jsxs(Fragment, { children: [announceTextContent && /* @__PURE__ */ jsx(ToastAnnounce, {
		scope,
		role: "status",
		"aria-live": type === "foreground" ? "assertive" : "polite",
		"aria-atomic": true,
		children: announceTextContent
	}), /* @__PURE__ */ jsx(PortalItem, {
		hostName: viewportName !== null && viewportName !== void 0 ? viewportName : "default",
		children: /* @__PURE__ */ jsx(ToastInteractiveProvider, {
			scope,
			onClose: function() {
				handleClose();
			},
			children: /* @__PURE__ */ jsx(Dismissable, {
				onEscapeKeyDown: composeEventHandlers(onEscapeKeyDown, function() {
					if (!context.isFocusedToastEscapeKeyDownRef.current) handleClose();
					context.isFocusedToastEscapeKeyDownRef.current = false;
				}),
				children: /* @__PURE__ */ jsx(Theme, {
					contain: true,
					forceClassName: true,
					name: themeName,
					children: /* @__PURE__ */ jsx(AnimatedView, {
						...panResponder === null || panResponder === void 0 ? void 0 : panResponder.panHandlers,
						style: [{ margin: "auto" }, animatedStyles],
						children: /* @__PURE__ */ jsx(Collection.ItemSlot, {
							scope: context.toastScope,
							children: /* @__PURE__ */ jsx(ToastImplFrame, {
								role: "status",
								"aria-live": "off",
								"aria-atomic": true,
								"data-state": open ? "open" : "closed",
								"data-swipe-direction": context.swipeDirection,
								pointerEvents: "auto",
								"$platform-web": {
									touchAction: "none",
									userSelect: "none"
								},
								...toastProps,
								ref: composedRefs,
								...isWeb && { onKeyDown: composeEventHandlers(props.onKeyDown, function(event) {
									if (event.key !== "Escape") return;
									onEscapeKeyDown === null || onEscapeKeyDown === void 0 || onEscapeKeyDown(event);
									if (!event.defaultPrevented) {
										context.isFocusedToastEscapeKeyDownRef.current = true;
										handleClose();
									}
								}) }
							})
						})
					})
				})
			})
		}, props.id)
	})] });
});
ToastImpl.propTypes = { type(props) {
	if (props.type && !["foreground", "background"].includes(props.type)) {
		var error = `Invalid prop \`type\` supplied to \`${TOAST_NAME}\`. Expected \`foreground | background\`.`;
		return new Error(error);
	}
	return null;
} };
var isDeltaInDirection = function(delta, direction) {
	var threshold = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : 0;
	var deltaX = Math.abs(delta.x);
	var deltaY = Math.abs(delta.y);
	var isDeltaX = deltaX > deltaY;
	if (direction === "left" || direction === "right" || direction === "horizontal") return isDeltaX && deltaX > threshold;
	return !isDeltaX && deltaY > threshold;
};
function getAnnounceTextContent(container) {
	if (!isWeb) return "";
	var textContent = [];
	Array.from(container.childNodes).forEach(function(node) {
		if (node.nodeType === node.TEXT_NODE && node.textContent) textContent.push(node.textContent);
		if (isHTMLElement(node)) {
			var isHidden = node.ariaHidden || node.hidden || node.style.display === "none";
			var isExcluded = node.dataset.toastAnnounceExclude === "";
			if (!isHidden) if (isExcluded) {
				var altText = node.dataset.toastAnnounceAlt;
				if (altText) textContent.push(altText);
			} else textContent.push(...getAnnounceTextContent(node));
		}
	});
	return textContent;
}
function isHTMLElement(node) {
	return node.nodeType === node.ELEMENT_NODE;
}
var GESTURE_GRANT_THRESHOLD = 10;
var shouldGrantGestureMove = function(dir, param) {
	var { dx, dy } = param;
	if ((dir === "horizontal" || dir === "left") && dx < -GESTURE_GRANT_THRESHOLD) return true;
	if ((dir === "horizontal" || dir === "right") && dx > GESTURE_GRANT_THRESHOLD) return true;
	if ((dir === "vertical" || dir === "up") && dy > -GESTURE_GRANT_THRESHOLD) return true;
	if ((dir === "vertical" || dir === "down") && dy < GESTURE_GRANT_THRESHOLD) return true;
	return false;
};
var getGestureDistance = function(dir, param) {
	var { dx, dy } = param;
	var y = 0;
	var x = 0;
	if (dir === "horizontal") x = dx;
	else if (dir === "left") x = Math.min(0, dx);
	else if (dir === "right") x = Math.max(0, dx);
	else if (dir === "vertical") y = dy;
	else if (dir === "up") y = Math.min(0, dy);
	else if (dir === "down") y = Math.max(0, dy);
	return {
		x,
		y
	};
};

export { ToastImpl, ToastImplFrame, useToastInteractiveContext };